import { useState, useEffect, useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow
} from "../../../utils/generalUtils"
import { useSelector } from "react-redux"
import { usePermissionsList } from "../../../hooks/queries/usePermissions"
import useFetchSettings from "../../../hooks/useFetchSettings"

const RoleZoneInputs = ({ role, setRole, placeholder, label, hasWriteSchedulePermissions }) => (
	<div className="flex flex-col space-y-2">
		<label className="text-sm font-medium text-gray-700 text-left">{label}:</label>
		<input
			type="text"
			placeholder={placeholder}
			value={role}
			onChange={e => setRole(e.target.value)}
			className="w-auto px-3 py-1.5 text-gray-700 bg-gray-100 rounded-lg border-0 focus:outline-none focus:ring-0"
			disabled={!hasWriteSchedulePermissions}
		/>
	</div>
)

const ExtraRoleInfo = ({ setNotification }) => {
	const [zone1, setZone1] = useState("")
	const [zone2, setZone2] = useState("")
	const [zone3, setZone3] = useState("")
	const [zone4, setZone4] = useState("")
	const [triageRole, setTriageRole] = useState("")
	const [nurseSchedulers, setNurseSchedulers] = useState("")
	const [deskRole, setDeskRole] = useState("")
	const [resourceMA, setResourceMA] = useState("")
	const [resourceNurse, setResourceNurse] = useState("")
	const [clinicLeaders, setClinicLeaders] = useState("")
	const [lab, setLab] = useState("")
	const [pharmacy, setPharmacy] = useState("")
	const [pas, setPas] = useState("")
	const [asc, setAsc] = useState("")
	const [ft, setFt] = useState("")
	const [closingMd, setClosingMd] = useState("")
	const [onCallSupervisor, setOnCallSupervisor] = useState("")
	const { user, getAccessTokenSilently } = useAuth0()
	const { settingsData } = useFetchSettings()

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	const permissions = usePermissionsList()

	const hasWriteSchedulePermissions = permissions && permissions.includes("write:schedules")

	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)

	const nextDate = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)
	const date = isTomorrowMode
		? nextDate
		: getDateFormatted(getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates))

	// Load notes when the component mounts
	useEffect(() => {
		const loadNotes = async () => {
			try {
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/api/get-role-info?date=${date}`,
					{
						method: "GET",
						headers: {
							Authorization: `Bearer ${token}`
						}
					}
				)
				if (!response.ok) {
					throw new Error("Failed to load notes")
				}
				const data = await response.json()
				if (data.extraNotes) {
					setNurseSchedulers(data.extraNotes.nurseSchedulers || "")
					setDeskRole(data.extraNotes.deskRole || "")
					setTriageRole(data.extraNotes.triageRole || "")
					setResourceMA(data.extraNotes.resourceMA || "")
					setResourceNurse(data.extraNotes.resourceNurse || "")
					setClinicLeaders(data.extraNotes.clinicLeaders || "")
					setLab(data.extraNotes.lab || "")
					setPharmacy(data.extraNotes.pharmacy || "")
					setPas(data.extraNotes.pas || "")
					setAsc(data.extraNotes.asc || "")
					setFt(data.extraNotes.ft || "")
					setClosingMd(data.extraNotes.closingMd || "")
					setZone1(data.extraNotes.zone1 || "")
					setZone2(data.extraNotes.zone2 || "")
					setZone3(data.extraNotes.zone3 || "")
					setZone4(data.extraNotes.zone4 || "")
					setOnCallSupervisor(data.extraNotes.onCallSupervisor || "")
				}
			} catch (error) {
				console.error(error)
			}
		}
		loadNotes()
		// `getAccessTokenSilently` is stable across renders per @auth0/auth0-react.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [date])

	const handleSaveRoleInfo = useCallback(async () => {
		setNotification({
			visible: true,
			message: "Saving role info...",
			success: true
		})
		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
			const notesData = {
				nurseSchedulers,
				deskRole,
				triageRole,
				resourceMA,
				resourceNurse,
				clinicLeaders,
				lab,
				pharmacy,
				pas,
				asc,
				ft,
				closingMd,
				zone1,
				zone2,
				zone3,
				zone4,
				onCallSupervisor
			}
			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/save-role-info`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`
					},
					body: JSON.stringify({ extraNotes: notesData, date })
				}
			)
			if (!response.ok) {
				throw new Error("Failed to save notes")
			}
			setNotification({
				visible: true,
				message: "Extra notes saved successfully!",
				success: true
			})
			setTimeout(() => {
				setNotification(prev => ({ ...prev, visible: false }))
			}, 3000)
		} catch (error) {
			console.error(error)
		}
	}, [
		nurseSchedulers,
		deskRole,
		triageRole,
		resourceMA,
		resourceNurse,
		clinicLeaders,
		lab,
		pharmacy,
		pas,
		asc,
		ft,
		closingMd,
		zone1,
		zone2,
		zone3,
		zone4,
		onCallSupervisor,
		date,
		setNotification,
		getAccessTokenSilently
	])

	return (
		<div className="flex flex-col space-y-4 my-6">
			<div className="grid grid-cols-3 gap-4">
				<RoleZoneInputs
					role={zone1}
					setRole={setZone1}
					placeholder="Enter Zone 1"
					label="Zone 1 MA"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={zone2}
					setRole={setZone2}
					placeholder="Enter Zone 2"
					label="Zone 2 MA"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={zone3}
					setRole={setZone3}
					placeholder="Enter Zone 3"
					label="Zone 3 MA"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={zone4}
					setRole={setZone4}
					placeholder="Enter Zone 4"
					label="Zone 4 MA"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={triageRole}
					setRole={setTriageRole}
					placeholder="Enter Triage MA"
					label="Triage MA"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={resourceMA}
					setRole={setResourceMA}
					placeholder="Enter Resource MA"
					label="Resource MA"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={nurseSchedulers}
					setRole={setNurseSchedulers}
					placeholder="Enter Nurse Schedulers"
					label="Nurse Schedulers"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={resourceNurse}
					setRole={setResourceNurse}
					placeholder="Enter Resource Nurse"
					label="Resource Nurse"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={onCallSupervisor}
					setRole={setOnCallSupervisor}
					placeholder="Enter On Call Supervisor"
					label="On Call Supervisor"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={ft}
					setRole={setFt}
					placeholder="Enter FT"
					label="FT"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={lab}
					setRole={setLab}
					placeholder="Enter Lab RNs"
					label="Lab RN"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={pas}
					setRole={setPas}
					placeholder="Enter PAs"
					label="PAs"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={asc}
					setRole={setAsc}
					placeholder="Enter ASC"
					label="ASC"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={clinicLeaders}
					setRole={setClinicLeaders}
					placeholder="Enter Clinic Leaders (AM/PM)"
					label="Clinic Leaders (AM/PM)"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={closingMd}
					setRole={setClosingMd}
					placeholder="Enter Closing MD"
					label="Closing MD"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={deskRole}
					setRole={setDeskRole}
					placeholder="Enter Desk Role"
					label="Desk Role"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>

				<RoleZoneInputs
					role={pharmacy}
					setRole={setPharmacy}
					placeholder="Enter Pharmacy"
					label="Pharmacy"
					hasWriteSchedulePermissions={hasWriteSchedulePermissions}
				/>
			</div>
			{hasWriteSchedulePermissions && (
				<button
					onClick={handleSaveRoleInfo}
					className="bg-customBlue hover:bg-customBlue-dark text-white px-4 py-2 rounded-lg w-24 mx-auto class-to-remove"
				>
					Save
				</button>
			)}
		</div>
	)
}

export default ExtraRoleInfo
